import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import BottomNav, { type Tab } from './components/BottomNav';
import Dashboard from './screens/Dashboard';
import Instructions from './screens/Instructions';
import More from './screens/More';
import Payments from './screens/Payments';
import Purchase from './screens/Purchase';
import Referral from './screens/Referral';
import { T } from './styles/tokens';
import { initTelegram, tg } from './telegram/sdk';
import { api, tokenStore } from './api/client';

type AuthState = 'pending' | 'ready' | 'error';
type ModalScreen = 'referral' | 'payments' | null;

export interface ExtendTarget {
  subscriptionId: number;
  protocol: 'wireguard' | 'vless';
  deviceLabel: string | null;
}

export default function App() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [auth, setAuth] = useState<AuthState>('pending');
  const [authErr, setAuthErr] = useState<string>('');
  const [extend, setExtend] = useState<ExtendTarget | null>(null);
  const [modal, setModal] = useState<ModalScreen>(null);

  useEffect(() => {
    initTelegram();
    const initData = tg()?.initData;
    if (!initData) {
      setAuth('ready');
      return;
    }
    api.auth
      .tma(initData)
      .then((res) => {
        tokenStore.set(res.access);
        setAuth('ready');
      })
      .catch((e: Error) => {
        setAuthErr(e.message);
        setAuth('error');
      });
  }, []);

  if (auth === 'pending') {
    return <div style={s.splash}>Подключение...</div>;
  }
  if (auth === 'error') {
    return (
      <div style={s.splash}>
        <div style={s.errTitle}>Не удалось войти</div>
        <div style={s.errText}>{authErr}</div>
      </div>
    );
  }

  return (
    <div style={s.root}>
      <main style={s.body}>
        {modal === 'referral' ? (
          <Referral onBack={() => setModal(null)} />
        ) : modal === 'payments' ? (
          <Payments onBack={() => setModal(null)} />
        ) : (
          <>
            {tab === 'dashboard' && (
              <Dashboard
                onBuy={() => {
                  setExtend(null);
                  setTab('purchase');
                }}
                onExtend={(target) => {
                  setExtend(target);
                  setTab('purchase');
                }}
              />
            )}
            {tab === 'purchase' && (
              <Purchase
                extend={extend}
                onDone={() => {
                  setExtend(null);
                  setTab('dashboard');
                }}
              />
            )}
            {tab === 'instructions' && <Instructions />}
            {tab === 'more' && (
              <More
                onOpenReferral={() => setModal('referral')}
                onOpenPayments={() => setModal('payments')}
              />
            )}
          </>
        )}
      </main>
      {!modal && (
        <BottomNav
          active={tab}
          onChange={(t) => {
            if (t !== 'purchase') setExtend(null);
            setTab(t);
          }}
        />
      )}
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', background: T.bg, color: T.text, fontFamily: 'DM Sans, sans-serif' },
  body: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
  splash: { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, background: T.bg, color: T.text, fontFamily: 'DM Sans, sans-serif', textAlign: 'center', gap: 10 },
  errTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 18, color: T.text },
  errText: { fontSize: 13, color: T.textSec, fontFamily: 'DM Mono, monospace' },
};
